import type { ChangeEvent } from 'react';
import { format, parse, isValid } from 'date-fns';
import { it } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { useIsSafeMode } from '@/lib/safeMode';
import { DatePicker as RadixDatePicker } from '@/components/ui/date-picker';

export interface SafeDatePickerProps {
  /** Current date value */
  date?: Date;
  /** Callback when date changes */
  onSelect?: (date: Date | undefined) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  /** Force native date input regardless of safe mode */
  forceNative?: boolean;
  /** Min date allowed */
  minDate?: Date;
  /** Max date allowed */
  maxDate?: Date;
  /** Aria label for accessibility */
  'aria-label'?: string;
  /** Name attribute for forms */
  name?: string;
}

/**
 * Date picker that automatically switches to native <input type="date">
 * when Safe Mode is enabled.
 *
 * The native date picker:
 * - Works on all devices and browsers
 * - Uses OS-native UI (familiar to users)
 * - No portal/popover issues
 * - Full keyboard and accessibility support
 */
export function SafeDatePicker({
  date,
  onSelect,
  placeholder = 'Seleziona data',
  disabled,
  className,
  forceNative,
  minDate,
  maxDate,
  'aria-label': ariaLabel,
  name,
}: SafeDatePickerProps) {
  const isSafeMode = useIsSafeMode();

  if (forceNative || isSafeMode) {
    return (
      <NativeDatePicker
        date={date}
        onSelect={onSelect}
        placeholder={placeholder}
        disabled={disabled}
        className={className}
        minDate={minDate}
        maxDate={maxDate}
        aria-label={ariaLabel}
        name={name}
      />
    );
  }

  return (
    <RadixDatePicker
      date={date}
      onSelect={onSelect}
      placeholder={placeholder}
      disabled={disabled}
    />
  );
}

/**
 * Native date input - always works, no portals
 */
function NativeDatePicker({
  date,
  onSelect,
  placeholder,
  disabled,
  className,
  minDate,
  maxDate,
  'aria-label': ariaLabel,
  name,
}: SafeDatePickerProps) {
  // Format date for native input (YYYY-MM-DD)
  const inputValue = date && isValid(date) ? format(date, 'yyyy-MM-dd') : '';

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const dateString = e.target.value;
    if (!dateString) {
      onSelect?.(undefined);
      return;
    }

    const parsed = parse(dateString, 'yyyy-MM-dd', new Date());
    if (isValid(parsed)) {
      onSelect?.(parsed);
    }
  };

  return (
    <div className="relative">
      <input
        type="date"
        value={inputValue}
        onChange={handleChange}
        disabled={disabled}
        min={minDate ? format(minDate, 'yyyy-MM-dd') : undefined}
        max={maxDate ? format(maxDate, 'yyyy-MM-dd') : undefined}
        aria-label={ariaLabel || placeholder}
        name={name}
        className={cn(
          // Base styles matching shadcn DatePicker trigger
          'flex h-9 w-full items-center rounded-md',
          'border border-input bg-transparent px-3 py-2',
          'text-sm shadow-sm',
          'ring-offset-background',
          'focus:outline-none focus:ring-1 focus:ring-ring',
          'disabled:cursor-not-allowed disabled:opacity-50',
          // Native date specific
          'cursor-pointer',
          // Style the placeholder state
          !inputValue && 'text-muted-foreground',
          className
        )}
      />
      {/* Show formatted date below for clarity (native format varies by locale) */}
      {date && isValid(date) && (
        <span className="pointer-events-none absolute right-10 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
          {format(date, 'd MMM yyyy', { locale: it })}
        </span>
      )}
    </div>
  );
}

/**
 * Export for direct use
 */
export { NativeDatePicker };
